import { List as ListIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isLovablePreview } from "@/lib/utils/environment";

const PEOPLE_LISTS = [
  "Q4 Prospects",
  "Champions",
  "Bay Area Contacts",
  "Warm Intros",
  "AE Follow-ups",
  "Newsletter Subscribers",
];
const COMPANY_LISTS = [
  "Enterprise Targets",
  "Mid-Market",
  "West Coast Companies",
  "Strategic Accounts",
  "Competitor Watch",
];
const DEAL_LISTS = [
  "Q4 Pipeline",
  "Committed Q4",
  "Big Bets",
  "At Risk",
  "Renewals",
];

function hashString(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function pickLists(pool: string[], id: string): string[] {
  const h = hashString(id);
  const count = (h % 3) + 1; // 1-3 lists
  const picks: string[] = [];
  for (let i = 0; i < count; i++) {
    picks.push(pool[(h + i * 7) % pool.length]);
  }
  return Array.from(new Set(picks));
}

interface Props {
  type: "person" | "company" | "deal";
  id: string;
  compact?: boolean;
}

export default function EntityListBadges({ type, id, compact }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // List memberships shown here are fabricated demo data (hashed from the entity id).
  // Preview/demo mode only — never render invented lists against real production records.
  if (!isLovablePreview()) return null;
  const pool = type === "person" ? PEOPLE_LISTS : type === "company" ? COMPANY_LISTS : DEAL_LISTS;
  const route = type === "person" ? "/people" : type === "company" ? "/companies" : "/deals";
  const memberships = pickLists(pool, id);
  if (memberships.length === 0) return null;

  const items = memberships.map((name, i) => (
    <span key={name}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          navigate(`${route}?list=${encodeURIComponent(name)}`);
        }}
        className="text-accent font-medium hover:underline transition cursor-pointer"
      >
        {name}
      </button>
      {i < memberships.length - 1 && <span className="text-muted-foreground">,</span>}
    </span>
  ));

  if (compact) {
    return (
      <div className="flex flex-wrap gap-x-2 gap-y-1 text-xs items-center">
        {items}
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 text-sm">
      <ListIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground items-center">
        <span className="shrink-0">{t("lists.badgeLabel")}</span>
        {items}
      </div>
    </div>
  );
}
